// @flow
import type { ChildrenArray, Element, Node } from 'react';
import React from 'react';
import {
  usePropsValidation,
  type CompilerInjectedProps,
  type Location,
} from './validation/runtime-checks/index.js';
import { types as t } from './validation/runtime-checks/index.js';
import {
  ColumnInternal,
  RowContext,
  RowInternal,
  GutterInternal,
} from './grid.js';

import { Text, Bold, Italics } from './Text.js';
import {
  AlgebraicWorking,
  AlgebraicWorkingStepIndicator,
} from './AlgebraicWorking.js';
import BoxAndWhiskerPlot from './BoxAndWhiskerPlot.js';
import Latex from './Latex.js';
import { LatexTextAtom } from './LatexTextAtom.js';
import { CartesianPlane } from './CartesianPlane.js';
import DraftImage from './DraftImage.js';
import Image from './Image.js';
import { List } from './List.js';
import { MultipleChoice } from './MultipleChoice.js';
import { NumberLine } from './NumberLine.js';
import { PseudoCodeBlock } from './PseudoCodeBlock.js';
import { StemAndLeafPlot } from './StemAndLeafPlot.js';
import TabularData from './TabularData.js';
import { TwoColumn } from './Columns.js';
import { TwoColumnProof } from './TwoColumnProof.js';
import UglyLegacyImage from './UglyLegacyImage.js';

const lochmara = '#0875BE';
const semiBoldProximaNova = 600;

type PartsContextT = {|
  // Used by the Parts component to determine how many Parts
  // components are in its ancestor chain. For every depth level
  // we change the numbering scheme for that Parts component.
  partsDepth: number,
|};

const PartsContext = React.createContext<PartsContextT>({
  partsDepth: 0,
  // We propagate whether the Question is "aiFriendly" or not
  // through the tree so that Part components at any depth can
  // enforce special validation requirements for aiFriendly Questions.
  aiFriendlyQuestion: false,
});

// prettier-ignore
const romanNumerals = [
  "i", "ii", "iii", "iv", "v", "vi", "vii", "viii", "ix", "x",
  "xi", "xii", "xiii", "xiv", "xv", "xvi", "xvii", "xviii", "xix", "xx"
];
const getLabel = (depth, index) => {
  // First level will be english alphabet characters. 'a', 'b', ..., 'z'
  if (depth === 0) {
    if (index >= 26) throw new Error('Must use <= 26 parts');
    return String.fromCharCode(97 + index); // 97 is 'a'
  }

  // Next level will be roman numerals. 'i', 'ii', ..., 'xx'
  if (depth === 1) {
    // For simplicity we'll only support 20 roman numerals for now
    // TODO write a decimalToRomanNumeral converter
    if (index >= romanNumerals.length) {
      throw new Error(`Must use <= ${romanNumerals.length} parts`);
    }
    return romanNumerals[index];
  }

  // We don't want more than 2 levels of nesting
  throw new Error('Nesting more than 2 <Parts> is not supported');
};

const Parts = (props: {|
  columns: 1 | 2 | 4,
  children: ChildrenArray<Element<typeof Part>>,
  _isSuperPartsHack?: boolean,
  _aiFriendlyQuestion?: boolean,
  ...CompilerInjectedProps,
|}) => {
  const ctx = React.useContext(PartsContext);
  const rowContext = React.useContext(RowContext);
  const { failedValidation, addErrors } = usePropsValidation(props, Parts);
  const { columns, children } = props;

  const clonePartElement = (child, id) => {
    return React.cloneElement(child, {
      _autoLabel: getLabel(ctx.partsDepth, id),
      _isSuperPartsHack: props._isSuperPartsHack,
      _partsColumns: columns,
    });
  };
  /**
   * All columnts have a 1 column gap between them as a "synthetic" gutter
   * in 2 and 4 columnts there are 1 and 3 gutter columns each.
   *
   * 4 Column
   * +---+-+---+-+---+-+---+
   * |   | |   | |   | |   |
   * |   | |   | |   | |   |
   * |   | |   | |   | |   |
   * +---------------------+
   *
   * 2 Column
   * +---------------------+
   * |         | |         |
   * |         | |         |
   * |         | |         |
   * +---------+-+---------+
   */

  // TODO we have a bit of a problem in which we can't do a lot of this
  // safely if we have failed validation, but we'll get the condition
  // hooks calling errors if we bail out before here.
  const parts = React.Children.toArray(children);
  const rowsResult = failedValidation
    ? // If we have failed validation, don't try to split as it will blow up
      // in weird ways potentially. Pretend it "succeeded". We will block
      // rendering due to the validation errors and force the user to fix
      // them before we execute the real splitting logic.
      { success: true }
    : splitPartElementsIntoRows(columns, parts);

  React.useEffect(() => {
    if (failedValidation || !props._performValidation) return;
    if (rowsResult.success) return;
    addErrors([{ message: rowsResult.message, ...rowsResult.location }]);
  });

  if (failedValidation) return null;
  if (rowsResult.success === false) return null;

  // Our grid system places a 1 "grid column" gutter between each "part column".
  // So we remove (numPartCols - 1) from the available columns to figure out
  // how many grid columns are available for non-gutter content. Then we split
  // that equally between each part column. We floor it, to ensure we get an
  // equal, integer number of grid columns per part column.
  const numPartCols = props.columns;
  const numGridColsPerPartCol = Math.floor(
    (rowContext.availableColumns - (numPartCols - 1)) / numPartCols,
  );
  function gridColsForPart(partColSpan: 1 | 2 | 4) {
    const gutterCols = partColSpan - 1;
    const contentCols = partColSpan * numGridColsPerPartCol;
    return contentCols + gutterCols;
  }

  const { rows } = rowsResult;

  return (
    <div>
      <PartsContext.Provider
        value={{
          ...ctx,
          partsDepth: ctx.partsDepth + 1,
          // If we are a direct child of a Question with aiFriendly specified,
          // then _aiFriendlyQuestion will be set. Otherwise we use the
          // existing value from context that is either a) the default context
          // value; or b) the value set in our nearest <Parts> ancestor.
          aiFriendlyQuestion:
            props._aiFriendlyQuestion ?? ctx.aiFriendlyQuestion,
        }}
      >
        <div style={{ height: 16, width: '100%' }} />
        {rows.map((row, i) => (
          <RowInternal key={i} style={{ breakInside: 'avoid' }}>
            {row.map((part, j) => (
              <React.Fragment key={j}>
                <ColumnInternal
                  colSpan={gridColsForPart(part.props.colSpan ?? 1)}
                >
                  {clonePartElement(part, partIndex(rows, i, j))}
                </ColumnInternal>
                {j < row.length - 1 && (
                  <>
                    <GutterInternal />
                    <ColumnInternal colSpan={1} />
                  </>
                )}
              </React.Fragment>
            ))}
          </RowInternal>
        ))}
      </PartsContext.Provider>
    </div>
  );
};
Parts.spec = {
  columns: t.unionOfValues(1, 2, 4),
  children: t.childrenArray(t.elementType(Part)),
};

/**
 * Splits an array of Part elements into rows based on their colSpan and total
 * columns. Returns a result type that indicates success or failure, along with the
 * rows of Part elements or an error message and location.
 */
function splitPartElementsIntoRows(
  columns: number,
  partElements: Array<Element<typeof Part>>,
):
  | { success: true, rows: Array<Array<Element<typeof Part>>> }
  | { success: false, message: string, location: Location } {
  const rows: Array<Array<Element<typeof Part>>> = [];
  let currentRow: Array<Element<typeof Part>> = [];
  let colSpanSum = 0;

  for (const part of partElements) {
    const colSpan = part.props.colSpan ?? 1;

    // Check if adding this part would overflow the current row
    if (colSpanSum + colSpan > columns) {
      const remainingColumns = columns - colSpanSum;
      const rowId = rows.length + 1;
      return {
        success: false,
        message: `Part elements must perfectly fill a row. There are ${remainingColumns} column(s) in row ${rowId} left to fill, but this Part has colSpan ${colSpan}.`,
        location: part.props._sourceLocations.Part,
      };
    }

    currentRow.push(part);
    colSpanSum += colSpan;

    // If the row is exactly full, finalize it
    if (colSpanSum === columns) {
      rows.push(currentRow);
      currentRow = [];
      colSpanSum = 0;
    }
  }

  // Add the final (possibly incomplete) row
  if (currentRow.length > 0) rows.push(currentRow);

  // Check if the colSpan in each row is homogeneous
  for (const row of rows) {
    const colSpans = row.map((part) => part.props.colSpan ?? 1);
    const uniqueColSpans = new Set(colSpans);
    if (uniqueColSpans.size > 1) {
      const start = row[0].props._sourceLocations.Part;
      const end = row[row.length - 1].props._sourceLocations.Part;
      return {
        success: false,
        message: `Each row must have homogeneous colSpans`,
        location: {
          startLineNumber: start.startLineNumber,
          startColumn: start.startColumn,
          endLineNumber: end.endLineNumber,
          endColumn: end.endColumn,
        },
      };
    }
  }

  return { success: true, rows };
}

function partIndex(rows, rowIndex, withinRowIndex) {
  let count = 0;
  for (let i = 0; i < rowIndex; i++) count += rows[i].length;
  count += withinRowIndex;
  return count;
}

function Part(props: {|
  label?: string,
  colSpan?: 2 | 4,
  pathways?: Array<'easy' | 'medium' | 'hard'>,
  _isSuperPartsHack?: boolean,
  // This is the "auto label" for the part. This is a letter or roman numeral
  // and depends on both the depth, and the index of the part.
  _autoLabel?: string,
  _partsColumns?: 1 | 2 | 4,
  children: Node,
  ...CompilerInjectedProps,
|}) {
  const { availableColumns } = React.useContext(RowContext);
  const { failedValidation, addErrors, locations } = usePropsValidation(
    props,
    Part,
  );
  const { aiFriendlyQuestion } = React.useContext(PartsContext);

  // Ad-hoc validation to require explicit and correct labels in aiFriendly Questions
  React.useEffect(() => {
    if (failedValidation || !props._performValidation) return;
    if (!aiFriendlyQuestion) return;

    const { label, _autoLabel } = props;

    if (label === undefined) {
      addErrors([
        {
          message: 'Part must specify a `label` prop',
          ...locations.Part,
        },
      ]);
    } else if (label !== _autoLabel) {
      addErrors([
        {
          message: `"${label}" is invalid, must be set to "${_autoLabel}"`,
          ...locations.label,
        },
      ]);
    }
  });

  // Ad-hoc validation to prevent the use of pathways prop when this is a "super part"
  React.useEffect(() => {
    if (failedValidation || !props._performValidation) return;
    if (props._isSuperPartsHack && props.pathways !== undefined) {
      addErrors([
        {
          message: 'Super parts cannot have pathways',
          ...locations.pathways,
        },
      ]);
    }
  });

  // Ad-hoc validation to ensure that colSpan is valid for the number of columns
  // specified in the parent Parts component.
  // columns={1} => col span not allowed.
  // columns={2} => col span can only be 2.
  // columns={4} => col span can be 2 or 4.
  React.useEffect(() => {
    if (failedValidation || !props._performValidation) return;
    if (!props._partsColumns || !props.colSpan) return;

    const { _partsColumns, colSpan } = props;

    if (_partsColumns === 1) {
      addErrors([
        {
          message: 'Part cannot have colSpan when Parts has columns={1}',
          ...locations.colSpan,
        },
      ]);
      return;
    }

    const allowedColSpans = _partsColumns === 4 ? [2, 4] : [2];
    if (allowedColSpans.includes(colSpan) === false) {
      addErrors([
        {
          message: `Part colSpan must be ${allowedColSpans.join(' or ')}`,
          ...locations.colSpan,
        },
      ]);
    }
  });

  if (failedValidation) return null;

  // Use the explicit label prop if specified; otherwise, use the auto label.
  const label = props.label ?? props._autoLabel;

  return (
    <div style={{ marginBottom: 8 }}>
      <RowInternal>
        <ColumnInternal colSpan={1}>
          <span style={{ fontWeight: semiBoldProximaNova, color: lochmara }}>
            {label}
          </span>
        </ColumnInternal>
        <ColumnInternal colSpan={availableColumns - 1}>
          {props.children}
        </ColumnInternal>
      </RowInternal>
    </div>
  );
}
Part.spec = {
  label: t.optional(partLabelChecker),
  colSpan: t.optional(t.unionOfValues(2, 4)),
  pathways: t.optional(t.arrayOf(t.unionOfValues('easy', 'medium', 'hard'))),
  children: t.childrenArray(
    t.unionOfTypes(
      t.string,
      t.elementType(AlgebraicWorking),
      t.elementType(AlgebraicWorkingStepIndicator),
      t.elementType(Bold),
      t.elementType(BoxAndWhiskerPlot),
      t.elementType(CartesianPlane),
      t.elementType(DraftImage),
      t.elementType(Image),
      t.elementType(Italics),
      t.elementType(Latex),
      t.elementType(LatexTextAtom),
      t.elementType(List),
      t.elementType(MultipleChoice),
      t.elementType(NumberLine),
      t.elementType(Parts),
      t.elementType(PseudoCodeBlock),
      t.elementType(StemAndLeafPlot),
      t.elementType(TabularData),
      t.elementType(Text),
      t.elementType(TwoColumn),
      t.elementType(TwoColumnProof),
      t.elementType(UglyLegacyImage),
    ),
  ),
};

function partLabelChecker(props, propName) {
  const label = props[propName];

  // Ensure it is of type string
  const failure = t.string(props, propName);
  if (failure) return failure;

  // Type narrowing, t.string() check above should ensure this.
  // branch is never taken in practice.
  if (typeof label !== 'string') return null;

  // TODO in future we want to enforce lowercase labels so this should be removed
  const normalized = label.toLowerCase();
  if (
    romanNumerals.includes(normalized) === false &&
    normalized.match(/^[a-z]$/) === null
  ) {
    return {
      typeDescription: partLabelChecker.description,
      reason: `must be a single lowercase letter (a-z), or a roman numeral in range [i, xx]`,
    };
  }

  // Ensure consistent casing for roman numerals
  // TODO in future we want to enforce lowercase labels so this should be removed
  if (romanNumerals.includes(normalized)) {
    if (label.toLowerCase() !== label && label.toUpperCase() !== label) {
      return {
        typeDescription: partLabelChecker.description,
        reason: `roman numerals must user consistent casing`,
      };
    }
  }

  return null;
}
partLabelChecker.description = `Valid label string`;

export { Part, Parts };
